import { useCallback, useEffect, useMemo, useState } from 'react';
import { FORMAS_PAGAMENTO } from '@/features/financeiro/api/financeiroApi';
import { Field } from '@/shared/ui/Field';
import { InputMonetario } from '@/shared/ui/InputMonetario';
import { Modal } from '@/shared/ui/Modal';
import { SearchInput } from '@/shared/ui/SearchInput';
import { pdvApi, type CaixaResumo, type FiadoEmAberto } from '../api/pdvApi';
import { formatarDinheiro, textoDoErro } from '../formato';

type Props = {
  caixa: CaixaResumo;
  onRecebido: (info: { valor: number; naGaveta: boolean }) => void;
};

const dataFmt = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

/**
 * Fiado em aberto e recebimento pelo balcão.
 *
 * Existe porque receber a dívida só pelo Financeiro deixava o caixa
 * errado: o dinheiro entrava na gaveta e o fechamento acusava sobra. Aqui
 * as duas coisas acontecem juntas — a baixa no lançamento e a entrada no
 * caixa.
 */
export function PdvFiado({ caixa, onRecebido }: Props) {
  const [lista, setLista] = useState<FiadoEmAberto[]>([]);
  const [busca, setBusca] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [recebendo, setRecebendo] = useState<FiadoEmAberto | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setLista(await pdvApi.fiadoEmAberto());
      setErro(null);
    } catch (e) {
      setErro(textoDoErro(e));
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return lista;
    return lista.filter(
      (f) =>
        f.clienteNome.toLowerCase().includes(termo) ||
        (f.vendaNumero ?? '').toLowerCase().includes(termo),
    );
  }, [lista, busca]);

  const totalEmAberto = useMemo(
    () => Math.round(filtrados.reduce((acc, f) => acc + f.saldo, 0) * 100) / 100,
    [filtrados],
  );

  return (
    <div className="pdv-historico">
      <div className="pdv-historico__filtros">
        <SearchInput
          valor={busca}
          onChange={setBusca}
          placeholder="Nome do cliente ou número da venda"
          ariaLabel="Buscar dívida em aberto"
        />
      </div>

      {erro && (
        <p className="pdv-aviso pdv-aviso--erro" role="alert">
          {erro}
        </p>
      )}

      {carregando && lista.length === 0 && <p className="pdv-centro--vazio">Carregando…</p>}

      {!carregando && filtrados.length === 0 && (
        <div className="pdv-carrinho-vazio">
          <p>{busca ? 'Ninguém com esse nome deve.' : 'Ninguém está devendo.'}</p>
        </div>
      )}

      {filtrados.length > 0 && (
        <>
          <div className="pdv-total">
            <span className="pdv-total__rotulo">Em aberto</span>
            <strong className="pdv-total__valor">{formatarDinheiro(totalEmAberto)}</strong>
            <span className="pdv-total__desconto">
              {filtrados.length} dívida(s) · recebido em dinheiro entra na gaveta
            </span>
          </div>

          <ul className="pdv-historico__lista">
            {filtrados.map((f) => (
              <li key={f.lancamentoId} className="pdv-historico__item">
                <div className="pdv-historico__identidade">
                  <span className="pdv-historico__numero">
                    {f.clienteNome}
                    {f.diasAtraso > 0 && (
                      <span className="pdv-fiado__atraso">
                        {f.diasAtraso} dia(s) em atraso
                      </span>
                    )}
                  </span>
                  <span className="pdv-historico__cliente">
                    {f.vendaNumero ? `Venda ${f.vendaNumero}` : 'Venda no fiado'}
                  </span>
                </div>
                <div className="pdv-historico__meta">
                  <span>Vence {dataFmt.format(new Date(f.vencimento))}</span>
                  {f.valorPago > 0 && (
                    <span>
                      {formatarDinheiro(f.valorPago)} de {formatarDinheiro(f.valor)} já pagos
                    </span>
                  )}
                </div>
                <strong className="pdv-historico__valor">{formatarDinheiro(f.saldo)}</strong>
                <div className="pdv-historico__acoes">
                  <button
                    type="button"
                    className="btn btn--primary"
                    onClick={() => setRecebendo(f)}
                  >
                    Receber
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {recebendo && (
        <ModalReceber
          divida={recebendo}
          caixaId={caixa.id}
          onFechar={() => setRecebendo(null)}
          onRecebido={(info) => {
            setRecebendo(null);
            onRecebido(info);
            void carregar();
          }}
        />
      )}
    </div>
  );
}

function ModalReceber({
  divida,
  caixaId,
  onRecebido,
  onFechar,
}: {
  divida: FiadoEmAberto;
  caixaId: string;
  onRecebido: (info: { valor: number; naGaveta: boolean }) => void;
  onFechar: () => void;
}) {
  const [valor, setValor] = useState(divida.saldo);
  const [formaPagamento, setFormaPagamento] = useState('Dinheiro');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const restante = Math.round((divida.saldo - valor) * 100) / 100;

  async function receber(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    setSalvando(true);
    try {
      await pdvApi.receberFiado(caixaId, {
        lancamentoId: divida.lancamentoId,
        valor,
        formaPagamento,
      });
      onRecebido({ valor, naGaveta: formaPagamento === 'Dinheiro' });
    } catch (err) {
      setErro(textoDoErro(err));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal aberto titulo={`Receber de ${divida.clienteNome}`} onFechar={onFechar}>
      <form className="pdv-form-modal" onSubmit={receber}>
        <p className="pdv-cartao__ajuda">
          Deve {formatarDinheiro(divida.saldo)}
          {divida.vendaNumero ? ` da venda ${divida.vendaNumero}` : ''}.
        </p>

        <Field rotulo="Valor recebido" obrigatorio dica="Pode receber só uma parte — o resto continua em aberto.">
          {(id) => (
            <InputMonetario
              id={id}
              className="input pdv-num pdv-num--valor"
              valor={valor}
              onChange={setValor}
            />
          )}
        </Field>

        <Field rotulo="Forma de pagamento">
          {(id) => (
            <select
              id={id}
              className="input"
              value={formaPagamento}
              onChange={(e) => setFormaPagamento(e.target.value)}
            >
              {FORMAS_PAGAMENTO.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.label}
                </option>
              ))}
            </select>
          )}
        </Field>

        {formaPagamento === 'Dinheiro' ? (
          <p className="pdv-aviso pdv-aviso--sucesso" role="status">
            Entra na gaveta do caixa aberto e conta na conferência do fechamento.
          </p>
        ) : (
          <p className="pdv-cartao__ajuda">
            Cartão e Pix não passam pela gaveta — a baixa é registrada só no financeiro.
          </p>
        )}

        {restante > 0.005 && (
          <p className="pdv-aviso pdv-aviso--atencao" role="status">
            Ficam {formatarDinheiro(restante)} em aberto.
          </p>
        )}

        {erro && (
          <p className="pdv-aviso pdv-aviso--erro" role="alert">
            {erro}
          </p>
        )}

        <div className="pdv-form-modal__acoes">
          <button type="button" className="btn btn--ghost" onClick={onFechar}>
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn--primary"
            disabled={salvando || valor <= 0 || valor > divida.saldo + 0.005}
          >
            {salvando ? 'Recebendo…' : `Receber ${formatarDinheiro(valor)}`}
          </button>
        </div>
      </form>
    </Modal>
  );
}
